import moment from 'moment';
import { store } from '../../redux/store';
import * as userActions from '../../redux/actions/user';
import * as elementsActions from '../../redux/actions/elements';
import { orderTypeChangeModal,orderPickTypeChangeModal, orderBelowMinimumDeliveryAmount, noDeliveryAddressSelected, notItemInTheCart, orderLocationChangeModal } from './modals';
import { deliveryStatuses, orderMethods, orderTypeIds, orderTypeStrings } from '../constants/order';
import * as tagmanagerEvents from './tagmanagerEvents';
import dispatches from './order-dispatches';
import { defaultCurrency } from '../constants/currency';

export const clearCart = (action = null, ...args) => {
  const options = args[0] || {};
  const clearUserAddress = options.clearUserAddress ?? true;
  const remainingArgs = options.clearUserAddress !== undefined ? args.slice(1) : args;

  dispatches(store, userActions, elementsActions, clearUserAddress);

  if (action) {
    action(...remainingArgs);
  }
};

export const guardOrderChange = (isNewType, action, ...args) => {
    if (!isNewType) {
        return action(...args);
    }

    const storeState = store.getState();
    if (storeState.user.userCart && storeState.user.userCart.length > 0) {
        tagmanagerEvents.abandonCart(storeState.user.userCartId, storeState.user.userCart);
    }

    return store.dispatch(elementsActions.setModalObject(orderTypeChangeModal(clearCart, action, ...args)));
};

export const guardOrderChangeLocation = (isNewType, action, ...args) => {
    if (!isNewType) {
        return action(...args);
    }

    const storeState = store.getState();
    if (storeState.user.userCart && storeState.user.userCart.length > 0) {
        tagmanagerEvents.abandonCart(storeState.user.userCartId, storeState.user.userCart);
    }

    return store.dispatch(elementsActions.setModalObject(orderLocationChangeModal(clearCart, action, ...args)));
};

export const guardOrderChangeToPickup = (isNewType, action, ...args) => {
    if (!isNewType) {
        return action(...args);
    }

    const storeState = store.getState();
    if (storeState.user.userCart && storeState.user.userCart.length > 0) {
        tagmanagerEvents.abandonCart(storeState.user.userCartId, storeState.user.userCart);
    }

    return store.dispatch(elementsActions.setModalObject(orderPickTypeChangeModal(clearCart, action, ...args)));
};

export const isDoughProOrder = (orderTypeId) => {
    if (!orderTypeId) {
        return false;
    }

    return parseInt(orderTypeId, 10) === orderTypeIds.doughPro;
};

export const isDeliveryOrPickup = (userOrderMethod) => {
    return userOrderMethod === orderMethods.delivery || userOrderMethod === orderMethods.pickup;
};

export const getCartProductIds = (userCart) => {
    if (userCart && userCart.length > 0) {
        return userCart.map((item) => +item.product?.id).filter((id) => id);
    }
    return [];
}

export const getCartSubtotal = (userCart) => {
    if (userCart && userCart.length > 0) {
        return userCart
            .map((item) => {
                const itemPrice = item.quantity * item.price;
                let optionsPrice = 0;
                if (item?.productOptions && item.productOptions.length > 0){
                    optionsPrice = item.productOptions
                    .map(option => option.quantity * (option.optionPrice ? option.optionPrice : 0))
                    .reduce((a, b) => a + b, 0);
                }
                return itemPrice + optionsPrice;
            })
            .reduce((a, b) => a + b, 0);
    }
    return 0;
}

export const getAddedCartLines = (updated, previous) =>
    updated.filter((line) => {
        const prev = previous.find((p) => p.id === line.id);
        if (!prev) return true;
        return (
            line.quantity !== prev.quantity ||
            JSON.stringify(line.productOptions) !== JSON.stringify(prev.productOptions)
        );
    });

export const getCartLineQuantity = (userCart) =>
    (Array.isArray(userCart) ? userCart : []).reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
    );

export const getLoyaltyDiscountAmount = (order) => {
    if (order && order?.orderRedeemables?.length > 0) {
        return order.orderRedeemables.reduce((accumulator, currentValue) => {
            return accumulator + (currentValue?.amountSaved ?? 0);
        }, 0);
    }
    return 0;
}

export const checkoutOrderMinAmount = (orderMinAmount, currency = defaultCurrency.symbol) => {
    store.dispatch(elementsActions.setModalObject(
        orderBelowMinimumDeliveryAmount(orderMinAmount, currency)
    ));
};

export const checkoutWithNoItems = () => {
    store.dispatch(elementsActions.setModalObject(
        notItemInTheCart()
    ));
};

export const checkoutNoDeliveryAddressSelected = (action) => {
    store.dispatch(elementsActions.setModalObject(
        noDeliveryAddressSelected(action)
    ));
};

export const orderETA = (order) => {
  if (!order) {
    return 'Delivery in ASAP';
  }

  if (order.orderTypeId === orderTypeIds.shipping) {
    return 'Shipping to ';
  }

  return order.deliveryEstimate ? `Delivery in ${order.deliveryEstimate}`
    : "Delivery in ASAP";
};

export const getOrderTypeString = (order) => {
  return order.orderTypeId === orderTypeIds.delivery || order.orderTypeId === orderTypeIds.shipping
    ? orderTypeStrings.delivery
    : orderTypeStrings.pickup;
}

/**
 * Actual delivery / driver-returned instant from API (third-party dropoff or internal status_date_5).
 * @param {object|null|undefined} order
 * @returns {string|null}
 */
export const getOrderDeliveredInstant = order => {
  const raw = order?.deliveryStatus?.dropoffTime ?? order?.status?.delivered;
  if (raw == null || raw === '') {
    return null;
  }
  return moment(raw).isValid() ? raw : null;
};

export const getOrderScheduledDateString = (order) => {
    const isToday = (someDate) => {
      const today = new Date();
      return someDate.getDate() === today.getDate() &&
        someDate.getMonth() === today.getMonth() &&
        someDate.getFullYear() === today.getFullYear();
    };
    const isDelivery = order.orderTypeId === orderTypeIds.delivery || order.orderTypeId === orderTypeIds.shipping;

    const deliveredAt = getOrderDeliveredInstant(order);
    if (order?.status?.status >= deliveryStatuses.DRIVER_RETURNED && deliveredAt) {
      return isToday(new Date(deliveredAt))
        ? `Today, ${moment(deliveredAt).format('h:mm A')}`
        : moment(deliveredAt).format('MMM D, h:mm A');
    }

    if (order.deliveryTime === '0000-00-00 00:00:00' && isDelivery) {
        return 'ASAP';
    }
    const deliveryDate = order.deliveryTime === '0000-00-00 00:00:00' ? order.invoiceDate : order.deliveryTime;
    return isToday(new Date(deliveryDate))
      ? `Today, ${moment(deliveryDate).format('h:mm A')}`
      : moment(deliveryDate).format('MMM D, h:mm A');
}

export default guardOrderChange;

